import { Injectable, OnDestroy, Injector } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import _ from 'underscore';
import { uniqBy } from 'lodash';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { TagsService } from '../../../Common/Tags/services/tags.service';
import { TagGroupsService } from '../../../Common/Tags/services/tag-groups.service';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { socialFiltersEnum } from '../../Core/enums/social-filters.enum';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { FacetValueModel } from '../../../api/mentions/model/facet-value.model';
import { JournodexSourceModel } from '../../../Common/Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum } from '../../../api/mentions/enum/search-criteria-sources.enum';
import { Mention } from '../../../Common/Mentions/models/mention';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { FilterTypeTagCheckboxModel } from '../../../Monitor/Shared/components/TagFilterCheckboxes/filter-type-tag-checkbox.model';
import { SourceTypeEnum } from '../../../api/source/enums/source-type.enum';
import { MonitoringAdvancedBooleanModel } from '../../../Monitor/Shared/models/monitoring-advanced-boolean.model';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { MentionSearchQueryModel } from '../../../Monitor/Shared/models/mention-search-query.model';
import { MediaTypeModel } from '../../../Monitor/Shared/models/media-type.model';
import { KeywordFilterModeEnum, MonitoringKeywordFilterModel } from '../../../Monitor/Shared/models/monitoring-keyword-filter.model';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { MentionSearchFacetTypesEnum } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { FilterSectionEnum } from '../../../Common/Filter/enums/filter-section.enum';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { SourceGroupService } from '../../../Common/Sources/services/source-group.service';
import { ReportsSeriesMapperService } from '../../../Common/Reports/services/reports-series-mapper.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { ThemeService } from '../../Intelligence/services/theme.service';
import { MonitoringSeriesTypeEnum } from '../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { MediaTypeTreeFilterModel } from '../../../Common/Filter/models/media-type-tree-filter.model';
import { MediaTypeTreeFilterComponent } from '../../../Common/Filter/components/MediaTypesTreeFilter/media-type-tree-filter.component';
import { SelectedMediaTypesFilterModel } from '../../../Monitor/Shared/models/selected-media-types-filter.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { LinkedinSubTypeEnum } from '@Common/Basic/enums/linkedin-sub-type.enum';
import { TwitterSubTypeEnum } from '@Common/Basic/enums/twitter-sub-type.enum';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { SocialRoutingService } from './social-routing.service';
import { GeographyQueryModel } from '../../../api/mentions/model/geography-query.model';
import { IncludeExcludeEnum } from '../../../Common/Filter/enums/include-exclude.enum';
import { SearchMode } from '../../../Monitor/Shared/enums/search-mode.enum';
import { FilterOptionModel } from '../../../Common/Filter/models/filter-option.model';
import { SavedFilterService } from '@Common/Filter/services/saved-filter.service';
import { SavedFilter } from '@Common/Filter/models/saved-filter.model';
import { SavedFilterCard, SavedFilterTypeModel } from '@Common/Filter/components/SavedFilterWrapper/saved-filter-type.model';
import { SavedFilterTypeEnum } from '@Common/Filter/enums/saved-filter.enum';
import { monitoringFiltersEnum } from '@Monitor/Shared/enums/monitoring-filters.enum';
import { DiscoverListsFilterService } from '../../../Common/Filter/services/discover-lists-filter.service';
import { AbstractDashboardSearchService } from '@Monitor/Core/services/abstract-dashboard-search.service';
import { IndexeddbCoreCacheService } from '@Core/Cache/services/indexeddb-core-cache.service';
import { SentimentModel } from '@Monitor/Shared/models/sentiment.model';
import { BlueskySubTypeEnum } from '@Common/Basic/enums/bluesky-sub-type.enum';
import { MediaSubscriptionApiService } from '@api/mediaSubscriptions/media-subscription.api.service';
import { engagementFilterKeys, EngagementFilterModel } from '../../../Common/Filter/models/engagement-filter.model';


@Injectable()
export class SocialSearchService extends AbstractDashboardSearchService implements OnDestroy {

    protected readonly networkTypeFilter = new MediaTypeTreeFilterModel<MediaTypeModel>(
        socialFiltersEnum.networkType,
        'Social.filter.networkType',
        MediaTypeTreeFilterComponent,
        this.localizationService,
        [],
        new SelectedMediaTypesFilterModel([], false),
        FilterSectionEnum.CONTENT,
        true,
        true
    );

    protected readonly mediaContentFilter = new FilterTypeCheckboxModel<string>(
        socialFiltersEnum.mediaContent,
        'Social.filter.mediaContent',
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.CONTENT
    );

    // @TODO - add back tag, tagGroups, mediaContentFilter & prominence. excluded for first social release

    // order matters: https://agility-confluence.atlassian.net/wiki/spaces/AM/pages/661650/Social+Listening+-+Filters#Filter-Panel-Groupings
    protected allFilters: FilterTypeModel[] = [
        // Content Section
        this.keywords,
        this.authors,
        this.networkTypeFilter,
        this.languages,
        this.hashtags,

        // Location Section
        this.continent,
        this.country,
        this.province,
        this.city,
        this.county,

        // Analysis Section
        this.audience,
        this.sentiment,
        this.engagement,
        this.engagementRate,

        // Saved Filters
        this.savedFilters
    ];

    agentIds: number[] = null;
    protected agentIdsSubject$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>(this.agentIds);
    public agentIds$ = this.agentIdsSubject$.asObservable();
    activeFilters$: BehaviorSubject<FilterTypeModel[]> = new BehaviorSubject<FilterTypeModel[]>([]);
    isArchiveSearch: boolean = false;

    constructor(
        protected tagsService: TagsService,
        protected tagGroupsService: TagGroupsService,
        protected journodexService: JournodexRegionsService,
        protected mentionsApi: MentionsApi,
        protected mediaSubscriptionApiService: MediaSubscriptionApiService,
        protected currentUserStateService: CurrentUserStateService,
        protected reportSeriesMapperService: ReportsSeriesMapperService,
        protected sourceGroupService: SourceGroupService,
        protected localizationService: LocalizationService,
        protected themeService: ThemeService,
        protected currencyConversionService: CurrencyConversionService,
        protected socialRoutingService: SocialRoutingService,
        protected monitorConfigService: MonitorConfigService,
        protected savedFilterService: SavedFilterService,
        protected discoverListsFilterService: DiscoverListsFilterService,
        protected injector: Injector,
        protected cacheService: IndexeddbCoreCacheService,
    ) {
        super(
            tagsService,
            tagGroupsService,
            journodexService,
            mentionsApi,
            currentUserStateService,
            reportSeriesMapperService,
            sourceGroupService,
            localizationService,
            currencyConversionService,
            monitorConfigService,
            savedFilterService,
            discoverListsFilterService,
            injector,
            cacheService
        );


        this.setFilters(this.allFilters);

        this.subscriptions.push(
            this.primaryFacets$.subscribe(primaryFacets => this.setFilterOptions()),
            this.themeService.currentResource$.subscribe(currentTheme => this.agentIds = currentTheme?.subscriptionIds),
            this.socialRoutingService.isArchiveSearch$.subscribe(isArchiveSearch => this.isArchiveSearch = isArchiveSearch)
        );
    }

    /**
     * Sets up the filter options from the current list of facets
     */
    setFilterOptions() {
        if (!this.primaryFacets) {
            this.clearUnselectedCheckboxOptions(this.authors);
            this.clearUnselectedCheckboxOptions(this.sentiment);
            this.clearUnselectedCheckboxOptions(this.languages);
            this.clearUnselectedCheckboxOptions(this.hashtags);
            this.clearUnselectedCheckboxOptions(this.tags);
            this.clearUnselectedCheckboxOptions(this.tagGroups);
            this.clearUnselectedCheckboxOptions(this.continent);
            this.clearUnselectedCheckboxOptions(this.country);
            this.clearUnselectedCheckboxOptions(this.province);
            this.clearUnselectedCheckboxOptions(this.city);
            this.clearUnselectedCheckboxOptions(this.county);

            this.authors.loading = false;
            this.networkTypeFilter.loading = false;
            this.sentiment.loading = false;
            this.languages.loading = false;
            this.hashtags.loading = false;
            this.tags.loading = false;
            this.tagGroups.loading = false;
            this.continent.loading = true;
            this.country.loading = true;
            this.province.loading = true;
            this.city.loading = true;
            this.county.loading = true;

            return;
        }

        this.getProminenceOptions(this.prominence, this.primaryFacets.prominenceFacets);
        const mediaTypeFacetValue =  this.primaryFacets.facets.get(MentionSearchFacetTypesEnum.mediaType);

        this.primaryFacets.facets.forEach((value: FacetValueModel[], type: MentionSearchFacetTypesEnum) => {
            switch (type) {
                case MentionSearchFacetTypesEnum.mediaType:
                    this.getNetworkOptions(this.networkTypeFilter, value);
                    break;
                case MentionSearchFacetTypesEnum.tweetType:
                    this.getTwitterSubtypeOptions(this.networkTypeFilter, value);
                    break;
                case MentionSearchFacetTypesEnum.linkedinType:
                    this.getLinkedinSubtypeOptions(this.networkTypeFilter, value, mediaTypeFacetValue);
                    break;
                case MentionSearchFacetTypesEnum.blueskyType:
                    this.getBlueskySubtypeOptions(this.networkTypeFilter, value, mediaTypeFacetValue);
                    break;
                case MentionSearchFacetTypesEnum.contact:
                    // @TODO: API is returning top 10k and that's really slow, truncate so we don't show so much
                    const contactValues = value.slice(0, 99);
                    this.getAuthorsOptions(this.authors, contactValues);
                    break;
                case MentionSearchFacetTypesEnum.audience:
                    break;
                case MentionSearchFacetTypesEnum.sentiment:
                    this.getSentimentsOptions(this.sentiment, value);
                    break;
                case MentionSearchFacetTypesEnum.language:
                    this.getLanguageOptions(this.languages, value);
                    break;
                case MentionSearchFacetTypesEnum.hashtags:
                    this.getHashtagOptions(this.hashtags, value);
                    break;

                /*
                @TODO: Check if this case is needed, I think it's just a boolean
                case MentionSearchFacetTypesEnum.mediaContent:
                    // @TODO
                    break;*/
                case MentionSearchFacetTypesEnum.tag:
                    this.getTagsOptions(value);
                    break;
                case MentionSearchFacetTypesEnum.tagGroup:
                    this.getTagGroupsOptions(value);
                    break;
                case MentionSearchFacetTypesEnum.continent:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.continent);
                    break;
                case MentionSearchFacetTypesEnum.country:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.country);
                    break;
                case MentionSearchFacetTypesEnum.province:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.province);
                    break;
                case MentionSearchFacetTypesEnum.city:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.city);
                    break;
                case MentionSearchFacetTypesEnum.county:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.county);
                    break;
            }
        });

        // clear the options if no facet is returned
        if (!this.primaryFacets.facets || this.primaryFacets.facets.size === 0) {
            this.clearAllFilters();
        }

        // reset filter loading for those filters without facet data
        this.filters.forEach(filter => filter.loading = false);

        this.setFilters(this.filters);
    }

    clearAllFilters() {
        this.networkTypeFilter.clear();
        this.networkTypeFilter.options = [];
        this.authors.clear();
        this.authors.options = [];
        this.sentiment.clear();
        this.sentiment.options = [];
        this.languages.clear();
        this.languages.options = [];
        this.hashtags.clear();
        this.hashtags.options = [];
        this.continent.clear();
        this.continent.options = [];
        this.country.clear();
        this.country.options = [];
        this.province.clear();
        this.province.options = [];
        this.city.clear();
        this.city.options = [];
        this.county.clear();
        this.county.options = [];
    }

    /**
     * Creates a search query from the current filters
     */
    generateFilterSearch(): MentionSearchCriteriaModel {
        const search = new MentionSearchQueryModel();

        search.dateQuery = this.dateQuery;

        const selectedMediaTypeFilter = this.networkTypeFilter.getCurrentValue();
        const twitterChildren = this.networkTypeFilter.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.TWITTER);
        })?.children || [];

        const linkedinChildren = this.networkTypeFilter.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.LINKEDIN);
        })?.children || [];

        const blueskyChildren = this.networkTypeFilter.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.BLUESKY);
        })?.children || [];

        if (this.networkTypeFilter.isInclude()) {
            search.includedMediaTypes = selectedMediaTypeFilter.mediaTypes;
            search.includedTweetTypes = twitterChildren.map(media => media.value);
            search.includedLinkedinTypes = linkedinChildren.map(media => media.value);
            search.includedBlueskyTypes = blueskyChildren.map(media => media.value);

            search.excludedMediaTypes = [];
            search.excludedTweetTypes = [];
            search.excludedLinkedinTypes = [];
            search.excludedBlueskyTypes = [];
        } else {
            search.includedMediaTypes = [];
            search.includedTweetTypes = [];
            search.includedLinkedinTypes = [];
            search.includedBlueskyTypes = [];

            let excludeAllX = true;
            let excludeAllLinkedIn = true;
            let excludeAllBluesky = true;

            Object.values(TwitterSubTypeEnum).forEach(type => {
                if (!twitterChildren.some(x => x.value.childId === type)) {
                    excludeAllX = false;
                }
            });

            Object.values(LinkedinSubTypeEnum).forEach(type => {
                if (!linkedinChildren.some(x => x.value.childId === type)) {
                    excludeAllLinkedIn = false;
                }
            });

            Object.values(BlueskySubTypeEnum).forEach(type => {
                if (!blueskyChildren.some(x => x.value.childId === type)) {
                    excludeAllBluesky = false;
                }
            });

            if (excludeAllX || !twitterChildren.length) {
                search.excludedTweetTypes = [];
                search.excludedMediaTypes = selectedMediaTypeFilter.mediaTypes;
            } else {
                search.excludedTweetTypes = twitterChildren.map(media => media.value);
                search.excludedMediaTypes = selectedMediaTypeFilter.mediaTypes.filter(media => media.id !== MediaTypeEnum.TWITTER);
            }

            if (excludeAllLinkedIn || !linkedinChildren.length) {
                search.excludedLinkedinTypes = [];
                search.excludedMediaTypes = selectedMediaTypeFilter.mediaTypes;
            } else {
                search.excludedLinkedinTypes = linkedinChildren.map(media => media.value);
                search.excludedMediaTypes = selectedMediaTypeFilter.mediaTypes.filter(media => media.id !== MediaTypeEnum.LINKEDIN);
            }

            if (excludeAllBluesky || !blueskyChildren.length) {
                search.excludedBlueskyTypes = [];
                search.excludedMediaTypes = selectedMediaTypeFilter.mediaTypes;
            } else {
                search.excludedBlueskyTypes = blueskyChildren.map(media => media.value);
                search.excludedMediaTypes = selectedMediaTypeFilter.mediaTypes.filter(media => media.id !== MediaTypeEnum.BLUESKY);
            }
        }

        if (this.keywords.getFilterMode() === KeywordFilterModeEnum.standard) {
            search.keywordGroups = this.keywords.getKeywordGroupsForSearchQuery();
            search.searchMode = SearchMode.standard;
        } else {
            search.expertBooleanQuery.filter = this.keywords.getCurrentValue().expertQuery.filter;
            search.expertBooleanQuery.searchContexts = [...this.keywords.getCurrentValue().expertQuery.searchContexts];
            search.searchMode = SearchMode.expert;
        }

        if (this.authors.isInclude()) {
            search.includedSources.push(...this.authors.getCurrentValue());
        } else {
            search.excludedSources.push(...this.authors.getCurrentValue());
        }
        search.audienceReach = this.audience.getCurrentValue();
        search.engagementFilter = {
            engagementRate: this.engagementRate.getCurrentValue(),
            ...this.engagement.getCurrentValue()
        };
        search.sentiments = this.sentiment.getCurrentValue();

        if (this.languages.isInclude()) {
            search.includedLanguages = this.languages.getCurrentValue();
        } else {
            search.excludedLanguages = this.languages.getCurrentValue();
        }

        if (this.hashtags.isInclude()) {
            search.hashtags.include = this.hashtags.getCurrentValue();
            search.hashtags.exclude = [];
        } else {
            search.hashtags.include = [];
            search.hashtags.exclude = this.hashtags.getCurrentValue();
        }

        if (this.continent.isInclude()) {
            search.includedRegions.push(...this.continent.getCurrentValue());
        } else {
            search.excludedRegions.push(...this.continent.getCurrentValue());
        }

        if (this.country.isInclude()) {
            search.includedRegions.push(...this.country.getCurrentValue());
        } else {
            search.excludedRegions.push(...this.country.getCurrentValue());
        }

        if (this.province.isInclude()) {
            search.includedRegions.push(...this.province.getCurrentValue());
        } else {
            search.excludedRegions.push(...this.province.getCurrentValue());
        }

        if (this.city.isInclude()) {
            search.includedRegions.push(...this.city.getCurrentValue());
        } else {
            search.excludedRegions.push(...this.city.getCurrentValue());
        }

        if (this.county.isInclude()) {
            search.includedRegions.push(...this.county.getCurrentValue());
        } else {
            search.excludedRegions.push(...this.county.getCurrentValue());
        }

        // @TODO: Figure out mediaContent filter
        // search.mediaContent.include = this.mediaContentFilter.getCurrentValue();

        search.agent.include = this.agentIds;

        if (this.tagGroups.isInclude()) {
            search.tagGroup.include = this.tagGroups.getCurrentValue();
        } else {
            search.tagGroup.exclude = this.tagGroups.getCurrentValue();
        }

        const tagFilter = <FilterTypeTagCheckboxModel>this.filters.find(filter => filter === this.tags);
        if (tagFilter) {
            if (this.tags.isInclude()) {
                search.tag.include = this.tags.getCurrentValue();
            } else {
                search.tag.exclude = this.tags.getCurrentValue();
            }
        }

        // MentionSearchCriteriaModel is the frontend model that wraps the frontend model MentionSearchQueryModel
        const criteria = new MentionSearchCriteriaModel();
        criteria.showSimilar = true;
        criteria.query = search;
        if (this.savedFilters.getValue().length > 0) {
            criteria.savedFilterIds = this.savedFilters.getValue().map(x => x.id);
        } else {
            criteria.savedFilterIds = [];
        }

        return criteria;
    }

    /**
     * Gets the filters from a search object and applies them to the current filters
     */
    populateFiltersFromSearch(search: MentionSearchQueryModel) {
        const authorsIncluded = search.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);
        const authorsExcluded = search.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);

        if (search.filterSources?.length > 0) {
            search.filterSources.forEach(source => {
                if (source.type === SourceTypeEnum.journalist) {
                    authorsIncluded.push({
                        id: source.value.id,
                        entityType: SearchCriteriaSourcesEnum.journalist,
                        // @HACK when constructing filterSources, we add a pair of double quotes for b/e - refer to D20061 NEP-8747
                        // Here we remove them to generate correct filter
                        entityName: source.value.name.slice(1, -1)
                    } as JournodexSourceModel);
                }
            });
        }

        if (authorsIncluded.length) {
            this.authors.options = authorsIncluded.map((author) => {
                return {
                    label: author.entityName,
                    value: author
                };
            });
            this.authors.setValue(authorsIncluded);
            this.authors.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (authorsExcluded.length) {
            this.authors.options = authorsExcluded.map((author) => {
                return {
                    label: author.entityName,
                    value: author
                };
            });
            this.authors.setValue(authorsExcluded);
            this.authors.setIncludeExclude(IncludeExcludeEnum.exclude);
        }


        // populate twitter parent with children
        if (search.includedTweetTypes.length > 0) {
            const twitterParent = search.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.TWITTER);
            if (twitterParent)  {
                twitterParent.children = search.includedTweetTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (search.excludedTweetTypes.length) {
            let twitterParent = search.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.TWITTER);
            if (!twitterParent) {
                twitterParent = new MediaTypeModel(MediaTypeEnum.TWITTER);
                search.excludedMediaTypes.push(twitterParent);
            }

            twitterParent.children = search.excludedTweetTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate linkedin parent with children
        if (search.includedLinkedinTypes.length > 0) {
            const linkedinParent = search.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.LINKEDIN);
            if (linkedinParent)  {
                linkedinParent.children = search.includedLinkedinTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (search.excludedLinkedinTypes.length) {
            let linkedinParent = search.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.LINKEDIN);
            if (!linkedinParent) {
                linkedinParent = new MediaTypeModel(MediaTypeEnum.LINKEDIN);
                search.excludedMediaTypes.push(linkedinParent);
            }

            linkedinParent.children = search.excludedLinkedinTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate bluesky parent with children
        if (search.includedBlueskyTypes.length > 0) {
            const blueskyParent = search.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.BLUESKY);
            if (blueskyParent)  {
                blueskyParent.children = search.includedBlueskyTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (search.excludedBlueskyTypes.length) {
            let blueskyParent = search.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.BLUESKY);
            if (!blueskyParent) {
                blueskyParent = new MediaTypeModel(MediaTypeEnum.BLUESKY);
                search.excludedMediaTypes.push(blueskyParent);
            }

            blueskyParent.children = search.excludedBlueskyTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }


        if (search.includedMediaTypes?.length) {
            this.networkTypeFilter.setValue(new SelectedMediaTypesFilterModel(search.includedMediaTypes, search.isArchivedMention));
            this.networkTypeFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (search.excludedMediaTypes?.length) {
            this.networkTypeFilter.setValue(new SelectedMediaTypesFilterModel(search.excludedMediaTypes, search.isArchivedMention));
            this.networkTypeFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (search.includedLanguages.length) {
            this.languages.options = search.includedLanguages.map((language) => {
                return {
                    label: language.name,
                    value: language
                };
            });
            this.languages.setValue(search.includedLanguages);
            this.languages.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (search.excludedLanguages.length) {
            this.languages.options = search.excludedLanguages.map((language) => {
                return {
                    label: language.name,
                    value: language
                };
            });
            this.languages.setValue(search.excludedLanguages);
            this.languages.setIncludeExclude(IncludeExcludeEnum.exclude);
        }


        this.audience.setValue(search.audienceReach);

        if (search.tag.include.length) {
            this.tags.loading = true;
            this.tags.setValue(search.tag.include);
            this.getTagsOptions(search.tag.include.map((tagId) => {
                return {
                    value: '' + tagId,
                    count: null
                };
            }));
            this.tags.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (search.tag.exclude.length) {
            this.tags.loading = true;
            this.tags.setValue(search.tag.exclude);
            this.getTagsOptions(search.tag.exclude.map((tagId) => {
                return {
                    value: '' + tagId,
                    count: null
                };
            }));
            this.tags.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (search.tagGroup.include.length) {
            this.tagGroups.loading = true;
            this.tagGroups.setValue(search.tagGroup.include);
            this.getTagGroupsOptions(search.tagGroup.include.map((tagGroupId) => {
                return {
                    value: '' + tagGroupId,
                    count: null
                };
            }));

            this.tagGroups.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (search.tagGroup.exclude.length) {
            this.tagGroups.loading = true;
            this.tagGroups.setValue(search.tagGroup.exclude);
            this.getTagGroupsOptions(search.tagGroup.exclude.map((tagGroupId) => {
                return {
                    value: '' + tagGroupId,
                    count: null
                };
            }));

            this.tagGroups.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (search.hashtags?.include?.length) {
            this.hashtags.loading = true;
            this.hashtags.setValue(search.hashtags.include);
            this.hashtags.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (search.hashtags?.include?.length) {
            this.hashtags.loading = true;
            this.hashtags.setValue(search.hashtags.exclude);
            this.hashtags.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (search.keywordGroups?.length > 0) {
            this.keywords.loading = true;
            if (search.searchMode === SearchMode.expert) {
                this.keywords.setValue(
                    {
                        keywordGroups:[],
                        expertQuery: search.expertBooleanQuery.filter !== '' ? search.expertBooleanQuery : new MonitoringAdvancedBooleanModel()
                    }
                );
            } else {
                this.keywords.setValue(
                    {
                        keywordGroups: search.keywordGroups,
                        expertQuery: new MonitoringAdvancedBooleanModel()
                    }
                );
            }
        }

        let rawIncludedRegions: GeographyQueryModel = search.rawIncludedRegions;
        if (MentionSearchQueryModel.isRawRegionEmpty(rawIncludedRegions) && search.includedRegions && search.includedRegions.length > 0) {
            rawIncludedRegions = MentionSearchQueryModel.extractRegions(search.includedRegions);
        }
        if (rawIncludedRegions) {
            const continentIds = rawIncludedRegions.continents;
            const countryIds = rawIncludedRegions.countries;
            const provinceIds = rawIncludedRegions.provinces;
            const cityIds = rawIncludedRegions.cities;
            const countyIds = rawIncludedRegions.counties;

            if (continentIds?.length) {
                this.populateRegionFiltersFromSearch(continentIds, GeographyTypesEnum.continent);
                this.continent.setIncludeExclude(IncludeExcludeEnum.include);
            }

            if (countryIds?.length) {
                this.populateRegionFiltersFromSearch(countryIds, GeographyTypesEnum.country);
                this.country.setIncludeExclude(IncludeExcludeEnum.include);
            }

            if (provinceIds.length) {
                this.populateRegionFiltersFromSearch(provinceIds, GeographyTypesEnum.province);
                this.province.setIncludeExclude(IncludeExcludeEnum.include);
            }

            if (cityIds?.length) {
                this.populateRegionFiltersFromSearch(cityIds, GeographyTypesEnum.city);
                this.city.setIncludeExclude(IncludeExcludeEnum.include);
            }

            if (countyIds?.length) {
                this.populateRegionFiltersFromSearch(countyIds, GeographyTypesEnum.county);
                this.county.setIncludeExclude(IncludeExcludeEnum.include);
            }
        }

        let rawExcludedRegions: GeographyQueryModel = search.rawExcludedRegions;
        if (MentionSearchQueryModel.isRawRegionEmpty(rawExcludedRegions) && search.excludedRegions && search.excludedRegions.length > 0) {
            rawExcludedRegions = MentionSearchQueryModel.extractRegions(search.excludedRegions);
        }
        if (rawExcludedRegions) {
            const continentIds = rawExcludedRegions.continents;
            const countryIds = rawExcludedRegions.countries;
            const provinceIds = rawExcludedRegions.provinces;
            const cityIds = rawExcludedRegions.cities;
            const countyIds = rawExcludedRegions.counties;

            if (continentIds?.length) {
                this.populateRegionFiltersFromSearch(continentIds, GeographyTypesEnum.continent);
                this.continent.setIncludeExclude(IncludeExcludeEnum.exclude);
            }

            if (countryIds?.length) {
                this.populateRegionFiltersFromSearch(countryIds, GeographyTypesEnum.country);
                this.country.setIncludeExclude(IncludeExcludeEnum.exclude);
            }

            if (provinceIds.length) {
                this.populateRegionFiltersFromSearch(provinceIds, GeographyTypesEnum.province);
                this.province.setIncludeExclude(IncludeExcludeEnum.exclude);
            }

            if (cityIds?.length) {
                this.populateRegionFiltersFromSearch(cityIds, GeographyTypesEnum.city);
                this.city.setIncludeExclude(IncludeExcludeEnum.exclude);
            }

            if (countyIds?.length) {
                this.populateRegionFiltersFromSearch(countyIds, GeographyTypesEnum.county);
                this.county.setIncludeExclude(IncludeExcludeEnum.exclude);
            }
        }

        // @TODO - update filter options

        // this.mediaContentFilter.setValue(search.mediaContent.include);
        // @TODO - update filter options

        this.engagementRate.setValue(search.engagementFilter.engagementRate);

        const engagementFilterValue = EngagementFilterModel.createBlankFilter();
        engagementFilterKeys.forEach(key => {
            engagementFilterValue[key] = search.engagementFilter[key];
        });
        this.engagement.setValue(engagementFilterValue);
        // @TODO - update filter options

        this.sentiment.setValue(search.sentiments);
        // @TODO - update filter options

    }

    fastSearch(search: MentionSearchCriteriaModel, filters?: MentionSearchCriteriaModel): Observable<MentionsSearchResponseModel> {
        const filteredSearch = MentionSearchCriteriaModel.createFiltered(search, filters, false);

        if (!filteredSearch.query?.includedMediaTypes?.length) {
            filteredSearch.query.includedMediaTypes = MediaTypeModel.getSocialMediaTypes();
        }

        filteredSearch.query.dateQuery = this.dateQuery;

        // Don't ask for expensive facets (hashtags, tweetTypes, linkedinType, blueskyType) in fast search,
        // they will be asked for in socialFacetSearch
        if (filteredSearch.facets.includes(MentionSearchFacetTypesEnum.hashtags)) {
            filteredSearch.facets.splice(filteredSearch.facets.indexOf(MentionSearchFacetTypesEnum.hashtags), 1);
        }

        if (filteredSearch.facets.includes(MentionSearchFacetTypesEnum.tweetType)) {
            filteredSearch.facets.splice(filteredSearch.facets.indexOf(MentionSearchFacetTypesEnum.tweetType), 1);
        }

        if (filteredSearch.facets.includes(MentionSearchFacetTypesEnum.linkedinType)) {
            filteredSearch.facets.splice(filteredSearch.facets.indexOf(MentionSearchFacetTypesEnum.linkedinType), 1);
        }

        if (filteredSearch.facets.includes(MentionSearchFacetTypesEnum.blueskyType)) {
            filteredSearch.facets.splice(filteredSearch.facets.indexOf(MentionSearchFacetTypesEnum.blueskyType), 1);
        }

        const searchCriteria = filteredSearch.getApiEntity();
        searchCriteria.requireCombinedQueryResults = true;

        if (searchCriteria.query.publicationDate && !searchCriteria.query.publicationDate.timeZone) {
            searchCriteria.query.publicationDate.timeZone = this.userTimezonePreference;
        }
        if (searchCriteria.query.harvestedDate && !searchCriteria.query.harvestedDate.timeZone) {
            searchCriteria.query.harvestedDate.timeZone = this.userTimezonePreference;
        }

        const searchRequest: Observable<MentionsSearchResponseModel> = this.mentionsApi.searchMentions(searchCriteria);

        return searchRequest.pipe(
            map(response => {
                this.mentions = response.mentions?.map(mention => new Mention(mention));
                this.mentions$.next(this.mentions);

                this.mentionCount = response.totalResults;
                this.mentionCount$.next(this.mentionCount);

                this.combinedQuery$.next(new MentionSearchQueryModel(response.combinedQuery));

                return response;
            }),
            catchError(error => {
                this.mentions = [];
                this.mentions$.next(this.mentions);

                this.mentionCount = 0;
                this.mentionCount$.next(this.mentionCount);

                throw error;
            })
        );
    }

    /**
     * Checks if there are filters that need to be re-applied after a new primary search is performed
     */
    protected override hasFiltersToApply(): boolean {
        // ignores date filter
        return (
            (
                this.keywords.getValue() &&
                (
                    this.keywords.getValue().keywordGroups.some(x => x.hasAnyKeywords())
                    || this.keywords.getValue().expertQuery.filter !== ''
                )
            )
            || this.networkTypeFilter.getValue().mediaTypes.length > 0
            || this.authors.getValue().length > 0
            || this.audience.chips.length > 0
            || this.engagement.hasValues()
            || this.engagementRate.chips.length > 0
            || this.sentiment.getValue().length > 0
            || this.languages.getValue().length > 0
            || this.hashtags.getValue().length > 0
            || this.mediaContentFilter.getValue().length > 0
            || this.tags.getValue().length > 0
            || this.tagGroups.getValue().length > 0
            || this.continent.getValue().length > 0
            || this.country.getValue().length > 0
            || this.province.getValue().length > 0
            || this.city.getValue().length > 0
            || this.county.getValue().length > 0
            || (this.prominence.getValue() && this.prominence.getValue().filters.length > 0)
            || this.savedFilters.getValue().length > 0
        );
    }

    /**
     * Populates the parent options for the network type filter
     * @param facets A list of facets from search results
    */
    protected getNetworkOptions(
        networkFilter: MediaTypeTreeFilterModel<MediaTypeModel>,
        facets: FacetValueModel[]
    ) {
        const parentNetworkTypes = MediaTypeModel.getAllMediaTypes();

        networkFilter.options = facets
            .map(facet => {
                const type = parentNetworkTypes.find(mediaType => mediaType.id === facet.value as MediaTypeEnum);

                return {
                    label: type?.name,
                    value: type,
                    count: facet.count
                };
            });
        // Setting sub options for some media types
        if (this.primaryFacets.facets.get(MentionSearchFacetTypesEnum.linkedinType)
            && networkFilter.options.find(x => x.value.id === MediaTypeEnum.LINKEDIN)?.value
        ) {
            networkFilter.options.find(x => x.value.id === MediaTypeEnum.LINKEDIN).value.children = this.linkedinFilterSubOptions;
        }

        if (this.primaryFacets.facets.get(MentionSearchFacetTypesEnum.blueskyType)
            && networkFilter.options.find(x => x.value.id === MediaTypeEnum.BLUESKY)?.value
        ) {
            networkFilter.options.find(x => x.value.id === MediaTypeEnum.BLUESKY).value.children = this.blueskyFilterSubOptions;
        }
        networkFilter.loading = false;
    }

    /**
     * Special case for network type filter, populates the twitter sub-options
     * @param mediaTypeFilter filter model
     * @param facets A list of facets from search results
     */
    protected getTwitterSubtypeOptions(
        networkFilter: MediaTypeTreeFilterModel<MediaTypeModel>,
        facets: FacetValueModel[]
    ) {

        const twitterTypes = MediaTypeModel.getAllTwitterSubTypes();
        const twitterParent = networkFilter.options.find(x => x.value.id === MediaTypeEnum.TWITTER);

        // find twitter parent then append children
        if (twitterParent) {
            twitterParent.value.children = facets.map(facet => {
                const type = twitterTypes.find(mediaType => mediaType.childId === facet.value as TwitterSubTypeEnum);
                return {
                    label: type?.name,
                    value: type,
                    count: facet.count,
                };
            });
        }

        networkFilter.loading = false;
    }

    /**
     * Special case for network type filter, populates the linkedIn sub-options
     * @param mediaTypeFilter filter model
     * @param facets A list of facets from search results
     *  @param mediaTypeFacetValue
     */
    protected getLinkedinSubtypeOptions(
        mediaTypeFilter: MediaTypeTreeFilterModel<MediaTypeModel>,
        facets: FacetValueModel[],
        mediaTypeFacetValue: FacetValueModel[]
    ) {
        this.getNetworkOptions(this.networkTypeFilter, mediaTypeFacetValue);
        super.getLinkedinSubtypeOptions(mediaTypeFilter, facets, mediaTypeFacetValue);
    }

    /**
     * Special case for network type filter, populates the bluesky sub-options
     * @param mediaTypeFilter filter model
     * @param facets A list of facets from search results
     *  @param mediaTypeFacetValue
     */
    protected getBlueskySubtypeOptions(
        mediaTypeFilter: MediaTypeTreeFilterModel<MediaTypeModel>,
        facets: FacetValueModel[],
        mediaTypeFacetValue: FacetValueModel[]
    ) {
        this.getNetworkOptions(this.networkTypeFilter, mediaTypeFacetValue);
        super.getBlueskySubtypeOptions(mediaTypeFilter, facets, mediaTypeFacetValue);
    }

    public setSearchCriteria(searchCriteria: MentionSearchCriteriaModel, sourceType?: MonitoringSeriesTypeEnum): void {
        this.searchCriteria = searchCriteria;
        if (this.searchCriteria) {
            this.searchCriteria.page = this.page;
            this.searchCriteria.pageSize = this.pageSize;
            this.searchCriteria.sortOrder = this.sortOrder;
            this.searchCriteria.sortField = this.sortField;
        }
        this.searchCriteria$.next(this.searchCriteria);
        this.setFilters(this.allFilters);
    }

    public search(refreshPrimaryFacets: boolean = false, refreshFilteredFacets: boolean = false): Observable<MentionsSearchResponseModel> {
        if (!this.agentIds?.length && !this.isArchiveSearch) {
            this.clearSearch();
            this.mentions = [];
            this.mentions$.next(this.mentions);

            return;
        }

        if (!this.searchCriteria) {
            this.searchCriteria = this.createMentionSearchCriteriaModel();
        }
        const filters = this.generateSearchFromFilters();
        this.activeFilters$.next(this.allFilters);

        const mainSearchRequest = super.search(false, false, filters);

        let primaryFacetRequest: Observable<MentionsSearchResponseModel>;
        if (refreshPrimaryFacets) {
            if (this.primaryFacetRequest) {
                this.primaryFacetRequest.unsubscribe();
            }

            primaryFacetRequest = this.socialFacetSearch(this.latestSearch);

            // just to make sure it runs, in case the caller doesn't subscribe
            this.primaryFacetRequest = primaryFacetRequest.subscribe({
                next: (result) => {
                    this.primaryFacetRequest = null;
                    this.setTotalResultsToFilter(result.totalResults);
                },
                error: (err) => {
                    this.primaryFacetRequest = null;
                    console.log(err);
                }
            });
        }

        return mainSearchRequest;
    }

    override updateKeywords(): void {
        const filterKeywords: string[] = [];

        if (this.keywords.getValue()) {
            const keywordGroups = this.keywords.getValue().keywordGroups;
            keywordGroups.forEach(keywordGroup => {
                filterKeywords.push(
                    ...keywordGroup.and,
                    ...keywordGroup.or
                );
            });
        }

        this.highlightKeywords  = [
            {
                keywords: filterKeywords,
                caseSensitive: this.searchCriteria.query.caseSensitive,
                class: 'agpr-highlight--social'
            }
        ];


        if (this.agentIds?.length) {
            this.mediaSubscriptionApiService.getByIds(this.agentIds).subscribe(agents => {
                const agentKeywords: string[] = [];
                agents.forEach(agent => {
                    if (agent.shouldTerms) {
                        agentKeywords.push(...agent.shouldTerms);
                    }
                });

                this.highlightKeywords = [{
                    keywords: [...filterKeywords, ...agentKeywords],
                    caseSensitive: this.searchCriteria.query.caseSensitive,
                    class: 'agpr-highlight--social'
                }];

                this.highlightKeywords$.next(this.highlightKeywords);
            });
        } else if (this.isArchiveSearch) {
            const archiveKeywords: string[] = [];

            // OR
            if (this.searchCriteria?.query?.socialSearchRequest?.anyTerms) {
                archiveKeywords.push(...this.searchCriteria?.query?.socialSearchRequest?.anyTerms);
            }
            // AND
            if (this.searchCriteria?.query?.socialSearchRequest?.mustAllTerms) {
                archiveKeywords.push(...this.searchCriteria?.query?.socialSearchRequest?.mustAllTerms);
            }

            this.highlightKeywords = [{
                keywords: [...filterKeywords, ...archiveKeywords],
                caseSensitive: this.searchCriteria.query.caseSensitive,
                class: 'agpr-highlight--social'
            }];

            this.highlightKeywords$.next(this.highlightKeywords);
        } else {
            this.highlightKeywords$.next(this.highlightKeywords);
        }
    }

    protected socialFacetSearch(search: MentionSearchCriteriaModel): Observable<MentionsSearchResponseModel> {
        search.filterQuery = null;

        search.pageSize = 0;
        search.facets = [
            MentionSearchFacetTypesEnum.sentiment,
            MentionSearchFacetTypesEnum.contact,
            MentionSearchFacetTypesEnum.language,
            MentionSearchFacetTypesEnum.mediaType,
            MentionSearchFacetTypesEnum.tweetType,
            MentionSearchFacetTypesEnum.hashtags,
            MentionSearchFacetTypesEnum.continent,
            MentionSearchFacetTypesEnum.country,
            MentionSearchFacetTypesEnum.province,
            MentionSearchFacetTypesEnum.city,
            MentionSearchFacetTypesEnum.county,
            MentionSearchFacetTypesEnum.linkedinType,
            MentionSearchFacetTypesEnum.blueskyType
        ];
        search.dateFacets = [];
        search.query.dateQuery = this.dateQuery;

        if (search.query.dateQuery && !search.query.dateQuery.timezone) {
            search.query.dateQuery.timezone = this.userTimezonePreference;
        }
        const searchRequest: Observable<MentionsSearchResponseModel> = this.mentionsApi.searchMentions(search.getApiEntity());

        return searchRequest.pipe(
            map(response => {
                this.primaryFacets = this.parseFacets(response);
                this.primaryFacets$.next(this.primaryFacets);

                return response;
            }),
            catchError(error => {

                throw error;
            })
        );
    }

    setAgents(ids: number[]) {
        this.agentIds = ids.slice();
        if (!this.searchCriteria) {
            this.searchCriteria = this.createMentionSearchCriteriaModel();
        }
        this.searchCriteria.query.agent.include = this.agentIds;
        this.agentIdsSubject$.next(this.agentIds);
    }

    clearAgents() {
        this.agentIds = [];
        if (this.searchCriteria) {
            this.searchCriteria.query.agent.include = [];
            this.agentIdsSubject$.next(this.agentIds);
        }
    }

    setDateQuery(dateQuery: DateQueryModel): void {
        this.dateQuery = dateQuery;
        this.dateQuery.timezone = this.userTimezonePreference;
        this.dateQuery$.next(this.dateQuery);
    }

    applyActiveFilters(filters: FilterTypeModel[]) {
        filters.forEach(activeFilter => {
            const matchFilter = this.allFilters.find(filter => filter.id === activeFilter.id);
            if (matchFilter) {
                if (matchFilter instanceof FilterTypeCheckboxModel || matchFilter instanceof MediaTypeTreeFilterModel) {
                    (matchFilter as FilterTypeCheckboxModel).options = (activeFilter as FilterTypeCheckboxModel).options;
                }

                if (matchFilter instanceof MonitoringKeywordFilterModel && activeFilter instanceof MonitoringKeywordFilterModel &&
                    activeFilter.getFilterMode() === KeywordFilterModeEnum.expert) {
                    matchFilter.setFilterMode(KeywordFilterModeEnum.expert);
                }
                if (matchFilter.id === this.savedFilters.id) {
                    const savedFilterValue = (activeFilter as SavedFilterTypeModel).getValue();
                    (matchFilter as SavedFilterTypeModel).filterCards = savedFilterValue?.map(
                        savedFilterModel => this.constructFilterCardFromValue(savedFilterModel)
                    );
                }
                matchFilter.setIncludeExclude(activeFilter.includeExclude);
                matchFilter.setValue(activeFilter.getValue());
            }
        });
        this.setFilters(this.allFilters);
        this.applyFilters(false);
    }

    generateSearchFromFilters(returnDefaultModel: boolean = true): MentionSearchCriteriaModel {
        if (this.hasFiltersToApply()) {
            return this.generateFilterSearch();
        } else {
            if (returnDefaultModel) {
                return this.createMentionSearchCriteriaModel();
            } else {
                return null;
            }
        }
    }

    clearSocialSearchRequest(): void {
        if (this.searchCriteria?.query) {
            delete this.searchCriteria.query.socialSearchRequest;
        }
    }

    prepareFilterModelToSave(): SavedFilter {
        const filterToSave = new SavedFilter();
        filterToSave.type = SavedFilterTypeEnum.monitoring;
        filterToSave.query = this.generateFilterSearch().query;

        // we don't want to save the agent in a save filter
        filterToSave.query.agent.include = [];
        filterToSave.showSimilar = true;
        return filterToSave;
    }

    constructFilterCardFromValue(val: SavedFilter): SavedFilterCard {
        const filterCard = super.constructFilterCardFromValue(val);
        const mediaTypeFilter = filterCard.filters.find(x => x.type === monitoringFiltersEnum.mediaTypes);
        if (mediaTypeFilter) {
            mediaTypeFilter.filterName =  'Social.filter.networkType';
        }
        return filterCard;
    }

    ungroupSavedFilter(savedFilter: SavedFilter): void {
        super.ungroupSavedFilter(savedFilter);
        if (this.networkTypeFilter.isInclude()) {
            this.networkTypeFilter.setCurrentValue(new SelectedMediaTypesFilterModel(
                uniqBy([...this.networkTypeFilter.getCurrentValue().mediaTypes, ...savedFilter.query.includedMediaTypes], 'id'), false)
            );
        } else {
            this.networkTypeFilter.setCurrentValue(new SelectedMediaTypesFilterModel(
                uniqBy([...this.networkTypeFilter.getCurrentValue().mediaTypes, ...savedFilter.query.excludedMediaTypes], 'id'), false)
            );
        }
    }

    createMentionSearchCriteriaModel(): MentionSearchCriteriaModel {
        const model = new MentionSearchCriteriaModel();
        model.query.sentiments = SentimentModel.getAll();
        return model;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
